import { useMemo, useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { FiestaBrandLogo } from '../components/FiestaBrandLogo'
import { authResetPassword } from '../api/auth'

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = useMemo(() => (params.get('token') || '').trim(), [params])
  const isConvite = useMemo(() => params.get('convite') === '1', [params])

  const [senha, setSenha] = useState('')
  const [confirma, setConfirma] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ senha?: string; confirma?: string }>({})

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setOkMsg(null)
    const next: { senha?: string; confirma?: string } = {}
    if (!senha) next.senha = 'Senha é obrigatória'
    else if (senha.length < 6) next.senha = 'Senha deve ter pelo menos 6 caracteres'
    if (senha !== confirma) next.confirma = 'As senhas não coincidem'
    setFieldErrors(next)
    if (Object.keys(next).length) return
    if (!token) {
      setError('Link inválido. Peça um novo em «Esqueci a senha».')
      return
    }
    setLoading(true)
    try {
      const msg = await authResetPassword(token, senha)
      setOkMsg(msg)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao redefinir senha')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <FiestaBrandLogo height={56} sx={{ mb: 2, maxWidth: 280 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {isConvite ? 'Bem-vindo ao Fiesta BI' : 'Nova senha'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            {isConvite
              ? 'Defina a senha da sua conta para começar a usar o Fiesta BI.'
              : 'Defina a nova senha da sua conta Fiesta BI.'}
          </Typography>

          {!token ? (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Link sem token. Use o link do e-mail ou peça um novo em «Esqueci a senha».
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          {okMsg ? (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {okMsg}
            </Alert>
          ) : null}

          {!okMsg ? (
            <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="senha"
                label="Nova senha"
                type="password"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => {
                  setSenha(e.target.value)
                  if (fieldErrors.senha) setFieldErrors((f) => ({ ...f, senha: undefined }))
                }}
                error={Boolean(fieldErrors.senha)}
                helperText={fieldErrors.senha || 'Mínimo 6 caracteres'}
                disabled={loading || !token}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirma"
                label="Confirmar nova senha"
                type="password"
                autoComplete="new-password"
                value={confirma}
                onChange={(e) => {
                  setConfirma(e.target.value)
                  if (fieldErrors.confirma) setFieldErrors((f) => ({ ...f, confirma: undefined }))
                }}
                error={Boolean(fieldErrors.confirma)}
                helperText={fieldErrors.confirma}
                disabled={loading || !token}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 2, py: 1.5 }}
                disabled={loading || !token}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Guardar senha'}
              </Button>
            </Box>
          ) : null}

          <Link component={RouterLink} to="/login" underline="hover" variant="body2">
            Ir para o login
          </Link>
        </Paper>
      </Box>
    </Container>
  )
}
